import { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { useAgencyClients } from "@/hooks/useAgencyClients";
import { useToast } from "@/hooks/use-toast";
import Sidebar from "@/components/sidebar";
import LeadJourneyDialog from "@/components/lead-journey-dialog";
import SalesKanban from "@/components/sales-kanban";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DropdownRangeDatePicker } from "@/components/ui/dropdown-range-date-picker";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Link } from "wouter";
import {
  Search,
  Users,
  Phone,
  ShoppingCart,
  PhoneCall,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronUp,
  ChevronDown,
  Activity,
  Download,
  RefreshCw,
  Tag,
  Filter,
  X,
  BadgeCheck,
  LayoutGrid,
} from "lucide-react";
import { format } from "date-fns";
import { pl } from "date-fns/locale";
import { qualificationOf } from "@shared/lead-status";
import { displayablePhone } from "@/lib/displayable-phone";
import { canonicalSource, looksLikeCampaignName } from "@shared/traffic-source";

// ─── Types ───────────────────────────────────────────────────────────────────

interface LeadRow {
  id: string;
  conversionType: string;
  email: string | null;
  phone: string | null;
  name: string | null;
  revenue: number | null;
  currency: string | null;
  orderId: string | null;
  source: string | null;
  medium: string | null;
  campaign: string | null;
  referrer: string | null;
  convertedAt: string | null;
  sourceCreatedAt: string | null;
  status: string | null;
  visitorId: string;
  trackingId: string;
  adId: string | null;
  adName: string | null;
  adCampaignName: string | null;
  adsetName: string | null;
  adThumbnailUrl: string | null;
  adAccountId: string | null;
  tags: string[];
}

interface LeadsResponse {
  leads: LeadRow[];
  page: number;
  limit: number;
  hasMore: boolean;
  total: number | null; // null when a search is active (filtered post-fetch)
}

interface SourcesResponse {
  sources: string[];
}

interface ConversionTagRow {
  id: string;
  name: string;
  slug: string;
  kind: string;
  color: string | null;
}

interface CalendlyStatusResponse {
  connected: boolean;
}

interface CalendlySyncSummary {
  scannedEvents: number;
  scannedInvitees: number;
  ingested: number;
  skippedDuplicate: number;
  skippedNoEmail: number;
  errors: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CONVERSION_TYPE_LABEL_KEYS: Record<string, string> = {
  lead: "leadsPage.conversionType.lead",
  booked_call: "leadsPage.conversionType.booked_call",
  qualified_call: "leadsPage.conversionType.qualified_call",
  checkout_started: "leadsPage.conversionType.checkout_started",
  purchase: "leadsPage.conversionType.purchase",
  upsell: "leadsPage.conversionType.upsell",
  refund: "leadsPage.conversionType.refund",
};

const CONVERSION_TYPE_COLORS: Record<string, string> = {
  lead: "bg-blue-500/15 text-blue-400 border border-blue-500/30",
  booked_call: "bg-purple-500/15 text-purple-400 border border-purple-500/30",
  qualified_call: "bg-cyan-500/15 text-cyan-400 border border-cyan-500/30",
  checkout_started: "bg-yellow-500/15 text-yellow-400 border border-yellow-500/30",
  purchase: "bg-green-500/15 text-green-400 border border-green-500/30",
  upsell: "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30",
  refund: "bg-red-500/15 text-red-400 border border-red-500/30",
};

const LEADS_PAGE_LIMIT = 25;

type DateField = "convertedAt" | "sourceCreatedAt";
type SortField =
  | "convertedAt"
  | "sourceCreatedAt"
  | "status"
  | "name"
  | "phone"
  | "source"
  | "type"
  | "revenue";

// Status filter buckets (mapped from raw GHL status by qualificationOf).
const STATUS_OPTIONS: { value: "qualified" | "unqualified"; labelKey: string }[] = [
  { value: "qualified", labelKey: "leadsPage.status.qualified" },
  { value: "unqualified", labelKey: "leadsPage.status.unqualified" },
];

// Conversion types that represent a scheduled meeting → have a "Data spotkania"
// (convertedAt = meeting time). Leads/purchases have no meeting → "—".
const APPOINTMENT_TYPES = new Set(["booked_call", "qualified_call"]);

type TabType = "all" | "lead" | "calls" | "purchase";

const TABS: { key: TabType; labelKey: string; icon: React.ReactNode; types?: string[] }[] = [
  { key: "all", labelKey: "leadsPage.tab.all", icon: <Activity className="w-3.5 h-3.5" /> },
  {
    key: "lead",
    labelKey: "leadsPage.tab.leads",
    icon: <Users className="w-3.5 h-3.5" />,
    types: ["lead"],
  },
  {
    key: "calls",
    labelKey: "leadsPage.tab.calls",
    icon: <PhoneCall className="w-3.5 h-3.5" />,
    types: ["booked_call", "qualified_call"],
  },
  // Refundy są tu, by tabela "Sprzedaż" netowała przychód tak samo jak kafel PRZYCHÓD (VP-139).
  // Renderowane jako wartość ujemna; nie liczą się jako sprzedaż (osobny czerwony badge "Zwrot").
  {
    key: "purchase",
    labelKey: "leadsPage.tab.purchase",
    icon: <ShoppingCart className="w-3.5 h-3.5" />,
    types: ["purchase", "upsell", "refund"],
  },
];

// ─── Channel classification ───────────────────────────────────────────────────

const PAID_CHANNEL_NAMES: Record<string, string> = {
  facebook: "Facebook Ads",
  fb: "Facebook Ads",
  meta: "Meta Ads",
  instagram: "Instagram Ads",
  ig: "Instagram Ads",
  google: "Google Ads",
  tiktok: "TikTok Ads",
  youtube: "YouTube Ads",
  linkedin: "LinkedIn Ads",
  twitter: "Twitter Ads",
  bing: "Bing Ads",
  pinterest: "Pinterest Ads",
  allegro: "Allegro Ads",
};

const SOCIAL_DOMAINS: Record<string, string> = {
  "facebook.com": "Facebook",
  "instagram.com": "Instagram",
  "twitter.com": "Twitter",
  "x.com": "Twitter",
  "linkedin.com": "LinkedIn",
  "tiktok.com": "TikTok",
  "youtube.com": "YouTube",
  "t.co": "Twitter",
  "lnkd.in": "LinkedIn",
  "l.facebook.com": "Facebook",
  "lm.facebook.com": "Facebook",
};

const SEARCH_ENGINE_DOMAINS = [
  "google.",
  "bing.com",
  "yahoo.com",
  "duckduckgo.com",
  "baidu.com",
  "yandex.",
];

const PAID_MEDIUMS = new Set([
  "paid",
  "cpc",
  "ppc",
  "cpm",
  "paidsocial",
  "paid_social",
  "display",
  "retargeting",
]);

function classifyChannel(lead: LeadRow): { channel: string; campaign: string | null } {
  // VP-243: gdy szablon UTM klienta wsadził nazwę kampanii w `utm_source`, nie jest to
  // kanał — wiersze sprzed backfillu wciąż ją tam mają. Traktujemy to jak brak źródła
  // i schodzimy niżej na referrer, zamiast wypisywać nazwę kampanii jako „Źródło".
  const rawSource = lead.source && !looksLikeCampaignName(lead.source) ? lead.source : null;
  const source = rawSource ? canonicalSource(rawSource) : undefined;
  const medium = lead.medium?.toLowerCase();
  // Nazwa kampanii z rozjechanego szablonu jest lepszą etykietą kampanii niż `utm_campaign`
  // (tam wylądowała nazwa reklamy), więc gdy ją wykryjemy — pokazujemy ją jako kampanię.
  const campaign =
    lead.source && looksLikeCampaignName(lead.source) ? lead.source : lead.campaign || null;

  if (source) {
    if (medium && PAID_MEDIUMS.has(medium)) {
      return { channel: PAID_CHANNEL_NAMES[source] ?? `${source} Ads`, campaign };
    }
    if (medium === "social" || medium === "organic_social") {
      return { channel: `${source.charAt(0).toUpperCase() + source.slice(1)} Organic`, campaign };
    }
    if (source === "email" || source === "newsletter" || medium === "email") {
      return { channel: "Email", campaign };
    }
    if (medium === "organic") {
      return { channel: "Organic Search", campaign };
    }
    if (medium === "referral") {
      return { channel: `Referral: ${source}`, campaign };
    }
    return { channel: `${source}${medium ? ` / ${medium}` : ""}`, campaign };
  }

  if (!lead.referrer) return { channel: "Direct / Webhook", campaign: null };

  try {
    const hostname = new URL(lead.referrer).hostname.replace(/^www\./, "");

    if (SEARCH_ENGINE_DOMAINS.some((se) => hostname.includes(se))) {
      return { channel: "Organic Search", campaign: null };
    }

    const socialMatch = Object.entries(SOCIAL_DOMAINS).find(([domain]) =>
      hostname.includes(domain)
    );
    if (socialMatch) {
      return { channel: `${socialMatch[1]} Organic`, campaign: null };
    }

    return { channel: hostname, campaign: null };
  } catch {
    return { channel: lead.referrer, campaign: null };
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return format(new Date(iso), "yyyy-MM-dd HH:mm", { locale: pl });
  } catch {
    return iso;
  }
}

// Compact two-line date (date + muted time) so date columns stay narrow.
function formatDateLines(iso: string | null): { d: string; t: string } | null {
  if (!iso) return null;
  try {
    const dt = new Date(iso);
    return { d: format(dt, "yyyy-MM-dd", { locale: pl }), t: format(dt, "HH:mm", { locale: pl }) };
  } catch {
    return null;
  }
}

function displayName(lead: LeadRow): string {
  // VP-243: telefon jako etykieta tylko gdy jest numerem — inaczej leciał tu
  // bełkot z nazwy kampanii ("++++5++18122025+") zapisany w polu telefonu.
  return lead.name || lead.email || displayablePhone(lead.phone) || lead.trackingId;
}

function tabTypesParam(tab: TabType): string | undefined {
  const found = TABS.find((t) => t.key === tab);
  if (!found || !found.types || found.types.length === 0) return undefined;
  return found.types.join(",");
}

// dd.MM.yyyy → end-of-day / start-of-day ISO for the date-range filter.
function toIsoStart(d: Date): string {
  const s = new Date(d);
  s.setHours(0, 0, 0, 0);
  return s.toISOString();
}
function toIsoEnd(d: Date): string {
  const e = new Date(d);
  e.setHours(23, 59, 59, 999);
  return e.toISOString();
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function LeadsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { selectedClientUserId, selectedClientId } = useAgencyClients();
  const [activeTab, setActiveTab] = useState<TabType>("all");
  const [viewMode, setViewMode] = useState<"list" | "kanban">("list");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [journeyLead, setJourneyLead] = useState<LeadRow | null>(null);

  // ── Hyros-like filters ──
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [selectedTagSlugs, setSelectedTagSlugs] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]); // 'qualified' | 'unqualified'
  const [dateField, setDateField] = useState<DateField>("convertedAt"); // which date the range filters
  const [sortBy, setSortBy] = useState<SortField>("convertedAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    clearTimeout((handleSearchChange as any)._t);
    (handleSearchChange as any)._t = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 350);
  }, []);

  const handleTabChange = useCallback((tab: TabType) => {
    setActiveTab(tab);
    setPage(1);
  }, []);

  const toggleSource = useCallback((src: string) => {
    setSelectedSources((prev) =>
      prev.includes(src) ? prev.filter((s) => s !== src) : [...prev, src]
    );
    setPage(1);
  }, []);

  const toggleTagSlug = useCallback((slug: string) => {
    setSelectedTagSlugs((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
    setPage(1);
  }, []);

  const handleDateApply = useCallback((from: Date | undefined, to: Date | undefined) => {
    setDateFrom(from);
    setDateTo(to);
    setPage(1);
  }, []);

  const toggleStatus = useCallback((s: string) => {
    setSelectedStatuses((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
    setPage(1);
  }, []);

  // Click a sortable header: toggle direction if same column, else switch column (desc).
  const handleSort = useCallback((col: SortField) => {
    setSortBy((prev) => {
      if (prev === col) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDir("desc");
      return col;
    });
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setDateFrom(undefined);
    setDateTo(undefined);
    setSelectedSources([]);
    setSelectedTagSlugs([]);
    setSelectedStatuses([]);
    setDateField("convertedAt");
    setPage(1);
  }, []);

  const typesParam = tabTypesParam(activeTab);
  const dateFromIso = dateFrom ? toIsoStart(dateFrom) : undefined;
  const dateToIso = dateTo ? toIsoEnd(dateTo) : undefined;
  const sourceParam = selectedSources.length > 0 ? selectedSources.join(",") : undefined;
  const tagsParam = selectedTagSlugs.length > 0 ? selectedTagSlugs.join(",") : undefined;
  const statusParam = selectedStatuses.length > 0 ? selectedStatuses.join(",") : undefined;
  const activeFilterCount =
    (dateFrom || dateTo ? 1 : 0) +
    selectedSources.length +
    selectedTagSlugs.length +
    selectedStatuses.length;

  const { data: calendlyStatus } = useQuery<CalendlyStatusResponse>({
    queryKey: ["/api/integrations/calendly/status"],
    queryFn: () => apiRequest("GET", "/api/integrations/calendly/status"),
    enabled: !!user,
    staleTime: 60_000,
  });

  // Distinct sources (lastUtmSource) for the source-filter dropdown.
  const { data: sourcesData } = useQuery<SourcesResponse>({
    queryKey: ["/api/tracking/leads/sources", { forClientUserId: selectedClientUserId }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (selectedClientUserId) params.set("forClientUserId", selectedClientUserId);
      const qs = params.toString();
      return apiRequest("GET", `/api/tracking/leads/sources${qs ? `?${qs}` : ""}`);
    },
    enabled: !!user,
    staleTime: 60_000,
  });
  const availableSources = sourcesData?.sources ?? [];

  // Conversion-tag taxonomy (slug + color) for the tag filter + table badges.
  const { data: tagDefs } = useQuery<ConversionTagRow[]>({
    queryKey: ["/api/conversion-tags/tags", { forClientUserId: selectedClientUserId }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (selectedClientUserId) params.set("forUserId", selectedClientUserId);
      const qs = params.toString();
      return apiRequest("GET", `/api/conversion-tags/tags${qs ? `?${qs}` : ""}`);
    },
    enabled: !!user,
    staleTime: 60_000,
  });
  const tagDefBySlug = (() => {
    const map = new Map<string, ConversionTagRow>();
    for (const tg of tagDefs ?? []) map.set(tg.slug.toLowerCase(), tg);
    return map;
  })();

  const { data, isLoading } = useQuery<LeadsResponse>({
    queryKey: [
      "/api/tracking/leads",
      {
        page,
        search: debouncedSearch,
        types: typesParam,
        dateFrom: dateFromIso,
        dateTo: dateToIso,
        dateField,
        source: sourceParam,
        tags: tagsParam,
        status: statusParam,
        sortBy,
        sortDir,
        forClientUserId: selectedClientUserId,
      },
    ],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), limit: String(LEADS_PAGE_LIMIT) });
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (typesParam) params.set("types", typesParam);
      if (dateFromIso) params.set("dateFrom", dateFromIso);
      if (dateToIso) params.set("dateTo", dateToIso);
      if (dateField !== "convertedAt") params.set("dateField", dateField);
      if (sourceParam) params.set("source", sourceParam);
      if (tagsParam) params.set("tags", tagsParam);
      if (statusParam) params.set("status", statusParam);
      params.set("sortBy", sortBy);
      params.set("sortDir", sortDir);
      if (selectedClientUserId) params.set("forClientUserId", selectedClientUserId);
      return apiRequest("GET", `/api/tracking/leads?${params.toString()}`);
    },
    enabled: !!user,
    staleTime: 30_000,
  });

  // Multi-type tabs (calls/purchase) are now filtered server-side via `types`,
  // so no JS re-filtering is needed — that path used to break pagination.
  const filteredLeads = data?.leads ?? [];

  // Numeracja porządkowa + zakres "Strona N (from-to)" liczone z realnego limitu/page.
  const pageNum = data?.page ?? page;
  const pageLimit = data?.limit ?? LEADS_PAGE_LIMIT;
  const rowOffset = (pageNum - 1) * pageLimit;
  const rangeFrom = filteredLeads.length ? rowOffset + 1 : 0;
  const rangeTo = rowOffset + filteredLeads.length;
  // Last page = ceil(total/limit). Unknown when total is null (search active) → "go to last" disabled.
  const lastPage = data?.total != null ? Math.max(1, Math.ceil(data.total / pageLimit)) : null;

  // Sort-direction caret for a sortable header (null when that column isn't active).
  const sortArrow = (col: SortField) =>
    sortBy === col ? (
      sortDir === "asc" ? (
        <ChevronUp className="inline w-3 h-3 ml-0.5" />
      ) : (
        <ChevronDown className="inline w-3 h-3 ml-0.5" />
      )
    ) : null;
  const SORTABLE_TH = "cursor-pointer select-none hover:text-white/70";

  const showLoading = isLoading;

  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStartedAt, setSyncStartedAt] = useState<number | null>(null);
  const [syncElapsedSec, setSyncElapsedSec] = useState(0);
  const [syncProgress, setSyncProgress] = useState<{
    phase: "scanning" | "importing" | null;
    formsCompleted: number;
    formsTotal: number;
    totalImported: number;
  } | null>(null);

  // Tick elapsed time every second while syncing so the button shows "1m 23s" live.
  useEffect(() => {
    if (!isSyncing || !syncStartedAt) {
      setSyncElapsedSec(0);
      return;
    }
    const tick = () => setSyncElapsedSec(Math.floor((Date.now() - syncStartedAt) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isSyncing, syncStartedAt]);

  const [isSyncingCalendly, setIsSyncingCalendly] = useState(false);
  const [isSyncingGhl, setIsSyncingGhl] = useState(false);

  // ─── Parent Lead linking (first-touch merge) ───
  const handleLinkParent = async (lead: LeadRow) => {
    const query = window.prompt(
      `Połącz "${lead.email || lead.phone || lead.trackingId}" jako CHILD do parent leada.\n\nWklej email LUB telefon parent leada (osoby z którą ten lead ma być scalony):`,
      ""
    );
    if (!query?.trim()) return;
    try {
      const lookupResp = (await apiRequest(
        "GET",
        `/api/tracking/visitors/lookup?q=${encodeURIComponent(query.trim().toLowerCase())}`
      )) as {
        visitors: Array<{
          id: string;
          email: string | null;
          phone: string | null;
          name: string | null;
        }>;
      };
      const visitors = lookupResp?.visitors ?? [];
      if (visitors.length === 0) {
        alert("Nie znaleziono visitora pod tym emailem/telefonem.");
        return;
      }
      let target = visitors[0];
      if (visitors.length > 1) {
        const choice = window.prompt(
          `Znaleziono ${visitors.length} kandydatów:\n\n` +
            visitors
              .map(
                (v, i) => `${i + 1}. ${v.email || v.phone || v.id} (${v.name || "brak imienia"})`
              )
              .join("\n") +
            `\n\nKtóry to parent? Wpisz numer 1-${visitors.length}:`,
          "1"
        );
        const idx = parseInt(choice || "1", 10) - 1;
        if (Number.isNaN(idx) || idx < 0 || idx >= visitors.length) return;
        target = visitors[idx];
      }
      if (target.id === lead.visitorId) {
        alert("To ten sam lead — nie można połączyć z samym sobą.");
        return;
      }
      const reason =
        window.prompt('Powód połączenia (opcjonalnie, np. "ten sam klient — drugi email")', "") ||
        undefined;
      await apiRequest("POST", `/api/tracking/visitors/${lead.visitorId}/link-parent`, {
        parentVisitorId: target.id,
        reason,
      });
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      alert(`✅ Połączono z parent: ${target.email || target.phone || target.id}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Błąd";
      alert(`Błąd: ${msg}`);
    }
  };

  const syncCalendly = useCallback(async () => {
    setIsSyncingCalendly(true);
    try {
      const summary: CalendlySyncSummary = await apiRequest(
        "POST",
        "/api/integrations/calendly/sync",
        {},
        { timeoutMs: 180_000 }
      );
      toast({
        title: t("leadsPage.toast.calendlySyncDone"),
        description: t("leadsPage.toast.calendlySyncDoneDesc", {
          ingested: summary.ingested,
          duplicates: summary.skippedDuplicate,
          events: summary.scannedEvents,
        }),
      });
      queryClient.invalidateQueries({ queryKey: ["/api/tracking/leads"] });
    } catch (err) {
      toast({
        title: t("leadsPage.toast.calendlySyncError"),
        description: err instanceof Error ? err.message : t("leadsPage.toast.unknownError"),
        variant: "destructive",
      });
    } finally {
      setIsSyncingCalendly(false);
    }
  }, [queryClient, toast, t]);

  const syncMetaLeads = useCallback(async () => {
    setIsSyncing(true);
    setSyncStartedAt(Date.now());
    try {
      // Kick off async sync — backend now returns jobId immediately and runs sync in background.
      // (Old in-line wait hit CloudFront's 60s origin response timeout when many forms/leads.)
      // Pass selected client so agency_owner imports into the client's account, not their own.
      // Always pass clientId so the sync is scoped to THIS client's Facebook Pages —
      // even a client with no linked user account (no forUserId) can't pull the
      // agency's whole page portfolio. forUserId still routes leads to the right user.
      const syncBody: { forUserId?: string; clientId?: string } = {};
      if (selectedClientUserId) syncBody.forUserId = selectedClientUserId;
      if (selectedClientId) syncBody.clientId = selectedClientId;
      const start = await apiRequest("POST", "/api/meta/leadgen/sync-all", syncBody);
      const jobId = start?.jobId;
      if (!jobId) throw new Error(t("leadsPage.toast.unknownError"));

      // Poll status every 3s until completed/failed. Cap at 10 minutes — server-side syncs
      // shouldn't take longer; if they do something is stuck and we bail out gracefully.
      const POLL_INTERVAL_MS = 3000;
      const MAX_POLL_MS = 10 * 60 * 1000;
      const startedAt = Date.now();

      while (true) {
        if (Date.now() - startedAt > MAX_POLL_MS) {
          throw new Error(t("leadsPage.toast.syncTimeoutDesc"));
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        const job = await apiRequest("GET", `/api/meta/leadgen/sync-status/${jobId}`);
        // Update live progress for UI (X/Y forms, Z leads imported)
        setSyncProgress({
          phase: job?.phase ?? null,
          formsCompleted: job?.formsCount ?? 0,
          formsTotal: job?.formsTotal ?? 0,
          totalImported: job?.totalImported ?? 0,
        });
        if (job?.status === "completed") {
          const totalImported = job.totalImported ?? 0;
          const totalSkipped = job.totalSkipped ?? 0;
          const errorCount = Array.isArray(job.summary?.errors) ? job.summary.errors.length : 0;
          toast({
            title: t("leadsPage.toast.syncDone"),
            description: errorCount
              ? t("leadsPage.toast.syncDoneDescWithErrors", {
                  imported: totalImported,
                  skipped: totalSkipped,
                  errors: errorCount,
                })
              : t("leadsPage.toast.syncDoneDesc", {
                  imported: totalImported,
                  skipped: totalSkipped,
                }),
          });
          queryClient.invalidateQueries({ queryKey: ["/api/tracking/leads"] });
          return;
        }
        if (job?.status === "failed") {
          throw new Error(job.errorMessage || t("leadsPage.toast.unknownError"));
        }
        // status === 'running' → keep polling
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t("leadsPage.toast.unknownError");
      toast({
        title: t("leadsPage.toast.syncError"),
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsSyncing(false);
      setSyncStartedAt(null);
      setSyncProgress(null);
    }
  }, [queryClient, toast, t, selectedClientUserId]);

  // Materialize GHL appointments -> booked_call so the Rozmowy tab matches the
  // dashboard (which reads GHL live). Per-client via forUserId.
  const syncGhlAppointments = useCallback(async () => {
    setIsSyncingGhl(true);
    try {
      const body: { forUserId?: string } = {};
      if (selectedClientUserId) body.forUserId = selectedClientUserId;
      const r = (await apiRequest(
        "POST",
        "/api/integrations/crm/appointment-pull/run-now",
        body
      )) as { created?: number; skipped?: number; fetched?: number };
      toast({
        title: "Rozmowy z GHL zsynchronizowane",
        description: `Dodano ${r?.created ?? 0}, pominieto ${r?.skipped ?? 0} (z ${r?.fetched ?? 0} appointmentow)`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/tracking/leads"] });
    } catch (err) {
      toast({
        title: "Blad synchronizacji rozmow GHL",
        description: err instanceof Error ? err.message : "Nieznany blad",
        variant: "destructive",
      });
    } finally {
      setIsSyncingGhl(false);
    }
  }, [queryClient, toast, selectedClientUserId]);

  // Format elapsed seconds as "1m 23s" or "45s".
  const formatElapsed = (sec: number): string => {
    if (sec < 60) return `${sec}s`;
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}m ${s.toString().padStart(2, "0")}s`;
  };

  const exportCsv = useCallback(() => {
    const header = t("leadsPage.csv.header");
    const rows = filteredLeads.map((l) => {
      const { channel, campaign } = classifyChannel(l);
      return [
        formatDate(l.convertedAt),
        t(CONVERSION_TYPE_LABEL_KEYS[l.conversionType] ?? l.conversionType),
        l.email ?? "",
        l.phone ?? "",
        l.name ?? "",
        channel,
        campaign ?? "",
        l.revenue != null ? String(l.revenue) : "",
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(",");
    });
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredLeads]);

  return (
    <div className="flex h-screen bg-[#030407] text-white overflow-hidden">
      <Sidebar />

      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-heading-2 font-extrabold text-white">Sales Data</h1>
              <p className="text-caption font-light text-white/40 mt-1">
                {t("leadsPage.subtitle")}
              </p>
            </div>
            <div className="flex items-start gap-2">
              <Link href="/tags">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-white/10 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white gap-2"
                >
                  <Tag className="w-3.5 h-3.5" />
                  Tagi
                </Button>
              </Link>
              {calendlyStatus?.connected && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={syncCalendly}
                  disabled={isSyncingCalendly}
                  className="border-white/10 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white gap-2"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isSyncingCalendly ? "animate-spin" : ""}`} />
                  {isSyncingCalendly ? t("leadsPage.syncing") : t("leadsPage.syncWithCalendly")}
                </Button>
              )}
              <div className="flex items-start gap-2">
                <div className="flex flex-col items-end gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={syncMetaLeads}
                    disabled={isSyncing}
                    className="border-white/10 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white gap-2"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${isSyncing ? "animate-spin" : ""}`} />
                    {isSyncing
                      ? `${t("leadsPage.syncing")} ${formatElapsed(syncElapsedSec)}`
                      : t("leadsPage.syncWithMeta")}
                  </Button>
                  {isSyncing &&
                    syncProgress &&
                    (syncProgress.formsTotal > 0 || syncProgress.totalImported > 0) && (
                      <p className="text-[11px] text-emerald-400 leading-tight">
                        {syncProgress.phase === "scanning"
                          ? `Skanuje strony Facebook: ${syncProgress.formsCompleted}/${syncProgress.formsTotal}`
                          : syncProgress.formsTotal > 0
                            ? `${syncProgress.formsCompleted}/${syncProgress.formsTotal} formularzy · ${syncProgress.totalImported} leadow`
                            : `${syncProgress.totalImported} leadow zaimportowanych`}
                      </p>
                    )}
                  {isSyncing && (
                    <p className="text-[10px] text-white/40 max-w-[280px] text-right leading-tight">
                      {t("leadsPage.toast.syncFirstTimeHint")}
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportCsv}
                  className="border-white/10 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white gap-2"
                >
                  <Download className="w-3.5 h-3.5" />
                  {t("leadsPage.exportCsv")}
                </Button>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={syncGhlAppointments}
                disabled={isSyncingGhl}
                title="Zaciagnij rozmowy (appointmenty) z GHL do Sales Data"
                className="border-white/10 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white gap-2"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isSyncingGhl ? "animate-spin" : ""}`} />
                {isSyncingGhl ? t("leadsPage.syncing") : "Rozmowy z GHL"}
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-6 border-b border-white/10 pb-0">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setViewMode("list");
                  handleTabChange(tab.key);
                }}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                  viewMode === "list" && activeTab === tab.key
                    ? "border-[#6E3AFF] text-white"
                    : "border-transparent text-white/40 hover:text-white/70"
                }`}
              >
                {tab.icon}
                {t(tab.labelKey)}
              </button>
            ))}
            {/* Zakladka CRM (mini-CRM etapow leadow) */}
            <button
              onClick={() => setViewMode("kanban")}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                viewMode === "kanban"
                  ? "border-[#6E3AFF] text-white"
                  : "border-transparent text-white/40 hover:text-white/70"
              }`}
            >
              <LayoutGrid className="w-4 h-4" />
              CRM
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#6E3AFF]/20 text-[#b79bff] text-[9px] font-bold uppercase tracking-wide leading-none">
                Nowość
              </span>
            </button>
          </div>

          {viewMode === "kanban" && (
            <SalesKanban forClientUserId={selectedClientUserId ?? undefined} />
          )}

          {viewMode === "list" && (
            <>
              {/* Filter bar (Hyros-like): date range + source + tags */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <div className="flex items-center gap-1.5 text-white/40 text-xs mr-1">
                  <Filter className="w-3.5 h-3.5" />
                  {t("leadsPage.filters.title")}
                </div>

                <DropdownRangeDatePicker
                  from={dateFrom}
                  to={dateTo}
                  label={t(
                    dateField === "sourceCreatedAt"
                      ? "leadsPage.filters.dateRangeAdded"
                      : "leadsPage.filters.dateRangeMeeting"
                  )}
                  onApply={handleDateApply}
                  className="border-white/10 bg-white/5 text-white/70 hover:bg-white/10 hover:text-white"
                />

                {/* Which date the range filters: meeting (convertedAt) vs added (sourceCreatedAt) */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 border-white/10 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white gap-1.5 text-xs"
                    >
                      {t("leadsPage.filters.dateField")}:{" "}
                      {t(
                        dateField === "sourceCreatedAt"
                          ? "leadsPage.col.dateAdded"
                          : "leadsPage.col.dateMeeting"
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="bg-[#0b0d14] border-white/10">
                    <DropdownMenuLabel className="text-white/50 text-xs">
                      {t("leadsPage.filters.dateField")}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuCheckboxItem
                      checked={dateField === "convertedAt"}
                      onCheckedChange={() => {
                        setDateField("convertedAt");
                        setPage(1);
                      }}
                      onSelect={(e) => e.preventDefault()}
                      className="text-white/80 text-xs"
                    >
                      {t("leadsPage.col.dateMeeting")}
                    </DropdownMenuCheckboxItem>
                    <DropdownMenuCheckboxItem
                      checked={dateField === "sourceCreatedAt"}
                      onCheckedChange={() => {
                        setDateField("sourceCreatedAt");
                        setPage(1);
                      }}
                      onSelect={(e) => e.preventDefault()}
                      className="text-white/80 text-xs"
                    >
                      {t("leadsPage.col.dateAdded")}
                    </DropdownMenuCheckboxItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Source multi-select */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 border-white/10 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white gap-1.5 text-xs"
                    >
                      {t("leadsPage.filters.source")}
                      {selectedSources.length > 0 && (
                        <span className="ml-1 px-1.5 rounded-full bg-[#6E3AFF]/30 text-white text-[10px]">
                          {selectedSources.length}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className="max-h-72 overflow-y-auto bg-[#0b0d14] border-white/10"
                  >
                    <DropdownMenuLabel className="text-white/50 text-xs">
                      {t("leadsPage.filters.source")}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-white/10" />
                    {availableSources.length === 0 && (
                      <div className="px-2 py-2 text-xs text-white/30">
                        {t("leadsPage.filters.noSources")}
                      </div>
                    )}
                    {availableSources.map((src) => (
                      <DropdownMenuCheckboxItem
                        key={src}
                        checked={selectedSources.includes(src)}
                        onCheckedChange={() => toggleSource(src)}
                        onSelect={(e) => e.preventDefault()}
                        className="text-white/80 text-xs"
                      >
                        {src}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Tag multi-select */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 border-white/10 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white gap-1.5 text-xs"
                    >
                      <Tag className="w-3.5 h-3.5" />
                      {t("leadsPage.filters.tags")}
                      {selectedTagSlugs.length > 0 && (
                        <span className="ml-1 px-1.5 rounded-full bg-[#6E3AFF]/30 text-white text-[10px]">
                          {selectedTagSlugs.length}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className="max-h-72 overflow-y-auto bg-[#0b0d14] border-white/10"
                  >
                    <DropdownMenuLabel className="text-white/50 text-xs">
                      {t("leadsPage.filters.tags")}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-white/10" />
                    {(tagDefs ?? []).length === 0 && (
                      <div className="px-2 py-2 text-xs text-white/30">
                        {t("leadsPage.filters.noTags")}
                      </div>
                    )}
                    {(tagDefs ?? []).map((tg) => (
                      <DropdownMenuCheckboxItem
                        key={tg.id}
                        checked={selectedTagSlugs.includes(tg.slug.toLowerCase())}
                        onCheckedChange={() => toggleTagSlug(tg.slug.toLowerCase())}
                        onSelect={(e) => e.preventDefault()}
                        className="text-white/80 text-xs"
                      >
                        <span
                          className="inline-block w-2 h-2 rounded-full mr-2"
                          style={{ backgroundColor: tg.color ?? "#6b7280" }}
                        />
                        {tg.name}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Status multi-select (qualified / unqualified) */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 border-white/10 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white gap-1.5 text-xs"
                    >
                      <BadgeCheck className="w-3.5 h-3.5" />
                      {t("leadsPage.filters.status")}
                      {selectedStatuses.length > 0 && (
                        <span className="ml-1 px-1.5 rounded-full bg-[#6E3AFF]/30 text-white text-[10px]">
                          {selectedStatuses.length}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="bg-[#0b0d14] border-white/10">
                    <DropdownMenuLabel className="text-white/50 text-xs">
                      {t("leadsPage.filters.status")}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-white/10" />
                    {STATUS_OPTIONS.map((opt) => (
                      <DropdownMenuCheckboxItem
                        key={opt.value}
                        checked={selectedStatuses.includes(opt.value)}
                        onCheckedChange={() => toggleStatus(opt.value)}
                        onSelect={(e) => e.preventDefault()}
                        className="text-white/80 text-xs"
                      >
                        {t(opt.labelKey)}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                {activeFilterCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="h-8 text-white/50 hover:text-white gap-1 text-xs"
                  >
                    <X className="w-3.5 h-3.5" />
                    {t("leadsPage.filters.clear")}
                  </Button>
                )}
              </div>

              {/* Search */}
              <div className="relative mb-5">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder={t("leadsPage.searchPlaceholder")}
                  className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/30 h-9"
                />
              </div>

              {/* Table */}
              <div className="rounded-xl border border-white/10 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 bg-white/[0.03] text-white/40 font-medium text-[11px] uppercase tracking-wider [&_th]:px-2 [&_th]:py-3">
                      <th className="text-right w-px whitespace-nowrap">{t("leadsPage.col.no")}</th>
                      <th
                        onClick={() => handleSort("sourceCreatedAt")}
                        className={`text-left w-20 ${SORTABLE_TH}`}
                      >
                        {t("leadsPage.col.dateAdded")}
                        {sortArrow("sourceCreatedAt")}
                      </th>
                      <th
                        onClick={() => handleSort("convertedAt")}
                        className={`text-left w-20 ${SORTABLE_TH}`}
                      >
                        {t("leadsPage.col.dateMeeting")}
                        {sortArrow("convertedAt")}
                      </th>
                      <th onClick={() => handleSort("name")} className={`text-left ${SORTABLE_TH}`}>
                        {t("leadsPage.col.lead")}
                        {sortArrow("name")}
                      </th>
                      <th
                        onClick={() => handleSort("phone")}
                        className={`text-left hidden md:table-cell ${SORTABLE_TH}`}
                      >
                        {t("leadsPage.col.phone")}
                        {sortArrow("phone")}
                      </th>
                      <th
                        onClick={() => handleSort("source")}
                        className={`text-left hidden lg:table-cell ${SORTABLE_TH}`}
                      >
                        {t("leadsPage.col.lastSource")}
                        {sortArrow("source")}
                      </th>
                      <th className="text-left hidden xl:table-cell">{t("leadsPage.col.ad")}</th>
                      <th className="text-left hidden lg:table-cell">{t("leadsPage.col.tags")}</th>
                      <th
                        onClick={() => handleSort("status")}
                        className={`text-left w-24 ${SORTABLE_TH}`}
                      >
                        {t("leadsPage.col.status")}
                        {sortArrow("status")}
                      </th>
                      <th
                        onClick={() => handleSort("type")}
                        className={`text-left w-20 ${SORTABLE_TH}`}
                      >
                        {t("leadsPage.col.type")}
                        {sortArrow("type")}
                      </th>
                      <th
                        onClick={() => handleSort("revenue")}
                        className={`text-right hidden md:table-cell w-24 ${SORTABLE_TH}`}
                      >
                        {t("leadsPage.col.revenue")}
                        {sortArrow("revenue")}
                      </th>
                      <th className="px-2 py-3 w-px whitespace-nowrap">
                        {t("leadsPage.col.link")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {showLoading && (
                      <tr>
                        <td colSpan={12} className="px-4 py-16 text-center text-white/30 text-sm">
                          {t("leadsPage.loading")}
                        </td>
                      </tr>
                    )}
                    {!showLoading && filteredLeads.length === 0 && (
                      <tr>
                        <td colSpan={12} className="px-4 py-16 text-center">
                          <div className="flex flex-col items-center gap-2">
                            <Users className="w-8 h-8 text-white/20" />
                            <p className="text-white/30 text-sm">{t("leadsPage.noData")}</p>
                            {debouncedSearch && (
                              <p className="text-white/20 text-xs">
                                {t("leadsPage.tryAnotherSearch")}
                              </p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    {filteredLeads.map((lead, i) => (
                      <tr
                        key={lead.id}
                        onClick={() => setJourneyLead(lead)}
                        className={`cursor-pointer transition-colors hover:bg-white/[0.04] [&_td]:px-2 [&_td]:py-2.5 ${
                          i < filteredLeads.length - 1 ? "border-b border-white/[0.06]" : ""
                        }`}
                      >
                        <td className="text-right text-white/30 text-xs whitespace-nowrap font-mono tabular-nums">
                          {rowOffset + i + 1}
                        </td>
                        <td className="text-white/50 text-[11px] whitespace-nowrap font-mono leading-tight">
                          {(() => {
                            const dt = formatDateLines(lead.sourceCreatedAt);
                            return dt ? (
                              <>
                                <div>{dt.d}</div>
                                <div className="text-white/30">{dt.t}</div>
                              </>
                            ) : (
                              "—"
                            );
                          })()}
                        </td>
                        <td className="text-white/50 text-[11px] whitespace-nowrap font-mono leading-tight">
                          {(() => {
                            if (!APPOINTMENT_TYPES.has(lead.conversionType))
                              return <span className="text-white/20">—</span>;
                            const dt = formatDateLines(lead.convertedAt);
                            return dt ? (
                              <>
                                <div>{dt.d}</div>
                                <div className="text-white/30">{dt.t}</div>
                              </>
                            ) : (
                              <span className="text-white/20">—</span>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-3">
                          <div className="font-medium text-white/90 truncate max-w-[140px]">
                            {displayName(lead)}
                          </div>
                          {lead.name && lead.email && (
                            <div className="text-xs text-white/40 truncate max-w-[140px]">
                              {lead.email}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3 text-white/50 text-xs hidden md:table-cell whitespace-nowrap">
                          {lead.phone ? (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {lead.phone}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-3 py-3 hidden lg:table-cell">
                          {(() => {
                            const { channel, campaign } = classifyChannel(lead);
                            return (
                              <div className="max-w-[150px]">
                                <span className="text-white/60 text-xs block truncate">
                                  {channel}
                                </span>
                                {campaign && (
                                  <span className="text-white/30 text-[10px] block truncate">
                                    {campaign}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-3 hidden xl:table-cell">
                          {lead.adId ? (
                            <div className="flex items-center gap-2 max-w-[150px]">
                              {lead.adThumbnailUrl ? (
                                <img
                                  src={lead.adThumbnailUrl}
                                  alt=""
                                  loading="lazy"
                                  className="w-7 h-7 rounded object-cover flex-shrink-0 bg-white/5"
                                />
                              ) : (
                                <div className="w-7 h-7 rounded flex-shrink-0 bg-white/5" />
                              )}
                              <div className="min-w-0">
                                <span
                                  className="text-white/70 text-xs block truncate"
                                  title={lead.adName || lead.adId}
                                >
                                  {lead.adName || `ad ${lead.adId}`}
                                </span>
                                {lead.adCampaignName && (
                                  <span
                                    className="text-white/30 text-[10px] block truncate"
                                    title={lead.adCampaignName}
                                  >
                                    {lead.adCampaignName}
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-white/20 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-3 py-3 hidden lg:table-cell">
                          {lead.tags && lead.tags.length > 0 ? (
                            <div className="flex flex-wrap gap-1 max-w-[200px]">
                              {lead.tags.slice(0, 4).map((slug) => {
                                const def = tagDefBySlug.get(slug.toLowerCase());
                                const color = def?.color ?? "#6b7280";
                                return (
                                  <span
                                    key={slug}
                                    className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium border"
                                    style={{
                                      color,
                                      borderColor: `${color}55`,
                                      backgroundColor: `${color}1a`,
                                    }}
                                    title={def?.name ?? slug}
                                  >
                                    {def?.name ?? slug}
                                  </span>
                                );
                              })}
                              {lead.tags.length > 4 && (
                                <span className="text-white/30 text-[10px]">
                                  +{lead.tags.length - 4}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-white/20 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          {(() => {
                            const q = qualificationOf(lead.status);
                            if (!q) return <span className="text-white/20 text-xs">—</span>;
                            return (
                              <span
                                className={`inline-flex justify-center text-center px-2 py-0.5 rounded-full text-[11px] font-medium ${q === "qualified" ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" : "bg-red-500/15 text-red-400 border border-red-500/30"}`}
                              >
                                {t(
                                  q === "qualified"
                                    ? "leadsPage.status.qualified"
                                    : "leadsPage.status.unqualified"
                                )}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-3">
                          <span
                            className={`inline-flex justify-center text-center px-2 py-0.5 rounded-full text-[11px] font-medium ${CONVERSION_TYPE_COLORS[lead.conversionType] ?? "bg-white/10 text-white/60"}`}
                          >
                            {t(
                              CONVERSION_TYPE_LABEL_KEYS[lead.conversionType] ?? lead.conversionType
                            )}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-right hidden md:table-cell">
                          {lead.revenue != null ? (
                            lead.conversionType === "refund" ? (
                              <span className="text-red-400 font-medium text-sm">
                                -{Math.abs(lead.revenue).toLocaleString("pl-PL")}{" "}
                                {lead.currency ?? "PLN"}
                              </span>
                            ) : (
                              <span className="text-green-400 font-medium text-sm">
                                {lead.revenue.toLocaleString("pl-PL")} {lead.currency ?? "PLN"}
                              </span>
                            )
                          ) : (
                            <span className="text-white/20">—</span>
                          )}
                        </td>
                        <td
                          className="px-2 py-3 text-right w-px whitespace-nowrap"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => handleLinkParent(lead)}
                            className="text-[11px] px-2 py-1 rounded border border-white/10 text-white/50 hover:text-white hover:border-white/30 hover:bg-white/5 transition"
                            title="Połącz z parent leadem (first-touch merge)"
                          >
                            Link
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <p className="text-xs text-white/30">
                  {!filteredLeads.length
                    ? t("leadsPage.page", { page: pageNum })
                    : data?.total != null
                      ? t("leadsPage.pageRangeTotal", {
                          page: pageNum,
                          from: rangeFrom,
                          to: rangeTo,
                          total: data.total,
                        })
                      : t("leadsPage.pageRange", { page: pageNum, from: rangeFrom, to: rangeTo })}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage(1)}
                    title={t("leadsPage.pagination.first")}
                    className="border-white/10 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white h-8 px-3"
                  >
                    <ChevronsLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    title={t("leadsPage.pagination.prev")}
                    className="border-white/10 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white h-8 px-3"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!data?.hasMore}
                    onClick={() => setPage((p) => p + 1)}
                    title={t("leadsPage.pagination.next")}
                    className="border-white/10 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white h-8 px-3"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={lastPage == null || page >= lastPage}
                    onClick={() => {
                      if (lastPage != null) setPage(lastPage);
                    }}
                    title={t("leadsPage.pagination.last")}
                    className="border-white/10 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white h-8 px-3"
                  >
                    <ChevronsRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Lead Journey Dialog */}
      {journeyLead && (
        <LeadJourneyDialog
          open={!!journeyLead}
          onClose={() => setJourneyLead(null)}
          email={journeyLead.email ?? undefined}
          phone={journeyLead.phone ?? undefined}
          name={journeyLead.name ?? undefined}
          forClientUserId={selectedClientUserId ?? undefined}
          adId={journeyLead.adId}
          adName={journeyLead.adName}
          adAccountId={journeyLead.adAccountId}
        />
      )}
    </div>
  );
}
